import { recruitingStageColors } from "../../utils/pipelineStages";
import { getCompanyLeadTitle } from "../../utils/companyLeadTitle";
import { getSalesStatusChipSx } from "../../utils/salesStatusColors";
import { notifyCompanyRelationshipsChanged } from "../../utils/companyRelationshipCounts";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TableCell,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Search as SearchIcon } from "../icons/FigmaControlIcons";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DeleteOutline as DeleteOutlineIcon } from "../icons/FigmaControlIcons";
import { Call, Sms } from "iconsax-react";

import SharedBulkActionsBar from "../BulkActionsBar";
import AssignTeamMemberAction from "../AssignTeamMemberAction";
import GenericDataTable from "../GenericDataTable";
import AddToSequenceDialog from "../PopupWindows/AddToSequenceDialog.jsx";
import { LinkedinBrandIcon } from "../InfoActionIcons";
import { buildJobSequenceItem, buildLeadSequenceItem } from "../../utils/sequenceItems";
import { normalizeContactSocialUrl } from "../../utils/contactChannels";
import { readStoredUser } from "../../utils/userSession";
import { formatRelativeTimeShort } from "../../utils/relativeTime";
import {
  CompanyTabSectionHeader,
  TAB_BLUE,
  TAB_PURPLE,
} from "./CompanyTabHeaderShared";
import { TAB_VIEW_CONTENT_WRAPPER_SX } from "../tabViewContentSpacing";
import {
  pillButtonFilledSx,
  pillButtonInteractiveSx,
  toolbarPillButtonSx,
  withPillButtonClassName,
} from "../../utils/pillButtonStyles";

const API_BASE = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const ADMIN_ROLES = new Set(["admin", "administrator", "system_user"]);
const TABLE_SELECTION_COLUMN_WIDTH_PX = 44;
const TABLE_SELECTION_COLUMN_INSET_PX = 22;
const TABLE_SELECTION_CHECKBOX_SIZE_PX = 18;

const LEAD_STATUS_OPTIONS = [
  "New",
  "Contacted",
  "Discovery",
  "Proposal",
  "Client",
  "Active",
  "Lost",
  "No stage",
  "Do not Contact",
];

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    "";

  return token
    ? {
        Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
        "x-access-token": token.replace(/^Bearer\s+/i, ""),
      }
    : {};
};

const safeText = (value, fallback = "") => {
  const normalized = String(value ?? "").trim();
  return normalized || fallback;
};

const hasLeadTableLabel = (row = {}) =>
  Boolean(safeText(row?.job_title || row?.lead_name || row?.title));

const buildPublicBase = (apiBase = "") => {
  const base = safeText(apiBase).replace(/\/$/, "");
  if (!base) return "";
  return base.endsWith("/api") ? base.slice(0, -4) : base;
};

const resolveAssetUrl = (value) => {
  const raw = safeText(value);
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;

  const publicBase = buildPublicBase(API_BASE);
  if (!publicBase) {
    return raw.startsWith("/") ? raw : `/${raw.replace(/^\/+/, "")}`;
  }

  if (raw.startsWith("/")) {
    return `${publicBase}${raw}`;
  }

  return `${publicBase}/${raw.replace(/^\/+/, "")}`;
};

const buildLeadRowsSignature = (rows = []) =>
  JSON.stringify(
    (Array.isArray(rows) ? rows : []).map((row) => [
      safeText(row?.id),
      safeText(row?.status).toLowerCase(),
      safeText(row?.updated_at || row?.created_at || row?.last_sync),
      safeText(row?.contact_name || row?.job_title || row?.name).toLowerCase(),
    ])
  );

const formatTimeAgo = (value) => {
  return formatRelativeTimeShort(value);
};

const isAdminRole = (value) => ADMIN_ROLES.has(safeText(value).toLowerCase());

const parseSkills = (value) => {
  if (Array.isArray(value)) return value.map((item) => safeText(item)).filter(Boolean);
  if (!value) return [];

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => safeText(item)).filter(Boolean);
      }
    } catch {
      return trimmed.split(",").map((item) => safeText(item)).filter(Boolean);
    }
  }

  return [];
};

const statusChipSx = (status) => String(status || "").toLowerCase() === "active"
  ? { backgroundColor: "#DCFCE7", color: "#15803D" }
  : getSalesStatusChipSx(status);

const candidateStatusColor = (status) => {
  const palette = recruitingStageColors(status);
  if (palette) return palette.color;
  const normalized = String(status || "").toLowerCase();

  if (["available", "open"].includes(normalized)) return "#22C55E";
  if (["rejected", "closed", "not available"].includes(normalized)) return "#EF4444";
  return TAB_BLUE;
};

const getLeadJobId = (row = {}) => {
  const candidateIds = [
    row?.job_id,
    row?.jobId,
    row?.related_job_id,
    row?.relatedJobId,
    row?.job?.id,
    row?.related_job?.id,
  ];

  for (const candidateId of candidateIds) {
    const jobId = Number(candidateId || 0);
    if (Number.isFinite(jobId) && jobId > 0) {
      return jobId;
    }
  }

  return 0;
};

const getLeadRecordId = (row = {}) => {
  const candidateIds = [
    row?.lead_id,
    row?.leadId,
    row?.id,
    row?.raw?.lead_id,
    row?.raw?.leadId,
    row?.raw?.id,
  ];

  for (const candidateId of candidateIds) {
    const leadId = Number(candidateId || 0);
    if (Number.isFinite(leadId) && leadId > 0) {
      return leadId;
    }
  }

  return 0;
};

const getLeadSelectionKey = (row = {}) => {
  const leadId = getLeadRecordId(row);
  if (leadId > 0) return String(leadId);
  return String(row?.id ?? row?.lead_id ?? row?.leadId ?? "");
};

const getNavigableLeadRow = (row = {}) => {
  const leadId = getLeadRecordId(row);
  if (!leadId || Number(row?.id || 0) === leadId) return row;
  return {
    ...row,
    id: leadId,
  };
};

const isSyntheticLeadRow = (row = {}) =>
  Number(row?.is_synthetic || row?.isSynthetic || row?.raw?.is_synthetic || 0) > 0 ||
  (getLeadRecordId(row) <= 0 && getLeadJobId(row) > 0);

const emptyActionButtonSx = {
  ...pillButtonFilledSx,
  ...pillButtonInteractiveSx,
  px: 2.6,
  minWidth: 130,
  height: 32,
  fontSize: 13,
  fontWeight: 400,
  boxShadow: "none",
};

const actionIconButtonSx = (enabled) => ({
  width: 28,
  height: 28,
  borderRadius: "999px",
  p: 0,
  color: TAB_PURPLE,
  opacity: enabled ? 1 : 0.35,
  border: "1px solid rgba(156, 79, 255, 0.22)",
  "&:hover": enabled
    ? {
        backgroundColor: "rgba(156, 79, 255, 0.08)",
      }
    : undefined,
});

function RowMenu({ onDelete, canDelete = false }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const closeMenu = (event) => {
    // The company lead table rows are clickable. Prevent Menu's backdrop
    // dismissal click from bubbling back to the row and opening that lead.
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <MoreVertIcon sx={{ fontSize: 18, color: "#6B7280" }} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        PaperProps={{
          sx: { borderRadius: 2, boxShadow: "0 12px 30px rgba(0,0,0,0.12)", minWidth: 160 },
        }}
      >
        {canDelete ? (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              setAnchorEl(null);
              onDelete?.();
            }}
            sx={{ gap: 1.25, color: "#B91C1C" }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            Delete
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
}

const LeadBulkActionsBar = ({
  selectedCount,
  onAddToSequence,
  onBulkDelete,
  onChangeStatus,
  onAssignTeamMember,
  disableAssignment = false,
  statusFeedback = null,
  onStatusFeedbackClose,
  showDelete = false,
  disableStatusChange = false,
  statusDisabledMessage = "",
}) => (
  <SharedBulkActionsBar
    selectedCount={selectedCount}
    selectionLabel="lead"
    actions={[
      {
        type: "button",
        label: "Add to Sequence",
        onClick: onAddToSequence,
      },
      { type: "divider" },
      {
        type: "node",
        key: "assign-team-member",
        node: (
          <AssignTeamMemberAction
            apiBase={API_BASE}
            selectedCount={selectedCount}
            disabled={disableAssignment}
            onAssign={onAssignTeamMember}
          />
        ),
      },
      { type: "divider" },
      {
        type: "menu",
        label: "Change Status",
        options: LEAD_STATUS_OPTIONS,
        onSelect: onChangeStatus,
        feedback: statusFeedback,
        onFeedbackClose: onStatusFeedbackClose,
        disabledMessage: statusDisabledMessage,
        disabled: disableStatusChange,
      },
      { type: "divider" },
      { type: "button", label: "Export" },
      showDelete ? { type: "divider" } : null,
      showDelete
        ? {
            type: "button",
            label: "Delete",
            onClick: onBulkDelete,
            disabled: selectedCount === 0,
          }
        : null,
    ].filter(Boolean)}
    rightButton={{
      label: "Match Candidates",
      background: "#9C4FFF",
      sx: toolbarPillButtonSx,
    }}
    sx={{ py: 1.5, px: 1 }}
  />
);

export default function CompanyLeadsSection({
  companyId = null,
  company = {},
  onAddNewLead,
  onSearchLead,
  onLeadsChange,
  onOpenLeadDetails,
  reloadKey = 0,
}) {
  const storedUser = useMemo(() => readStoredUser(), []);
  const isAdminUser = isAdminRole(storedUser?.role);
  const resolvedCompanyId = useMemo(() => {
    const rawValue =
      companyId ?? company?.company_id ?? company?.companyId ?? company?.id ?? null;
    const numericValue = Number(rawValue || 0);
    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 0;
  }, [companyId, company?.company_id, company?.companyId, company?.id]);
  const sourceLeadRows = useMemo(
    () =>
      (Array.isArray(company?.related_leads) ? company.related_leads : []).filter(
        hasLeadTableLabel
      ),
    [company?.related_leads]
  );
  const hasExplicitLeadRowsField = useMemo(
    () =>
      Boolean(
        company &&
          typeof company === "object" &&
          Object.prototype.hasOwnProperty.call(company, "related_leads")
      ),
    [company]
  );
  const hasExplicitLeadCountField = useMemo(
    () =>
      Boolean(
        company &&
          typeof company === "object" &&
          (Object.prototype.hasOwnProperty.call(company, "leadsCount") ||
            Object.prototype.hasOwnProperty.call(company, "leads_count"))
      ),
    [company]
  );
  const expectedLeadCount = Number(company?.leadsCount ?? company?.leads_count ?? 0) || 0;
  const hasUnlinkedJobRows = useMemo(
    () =>
      sourceLeadRows.some((row) => {
        const hasJobLikeLabel = Boolean(safeText(row?.job_title || row?.contact_name));
        return hasJobLikeLabel && getLeadJobId(row) <= 0;
      }),
    [sourceLeadRows]
  );
  const shouldHydrateLeadRows =
    (!sourceLeadRows.length && expectedLeadCount > 0) || hasUnlinkedJobRows;
  const shouldFetchAuthoritativeLeadRows = shouldHydrateLeadRows || reloadKey > 0;
  const [leadRows, setLeadRows] = useState(() =>
    sourceLeadRows
  );
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [isAddToSequenceOpen, setIsAddToSequenceOpen] = useState(false);
  const [statusToast, setStatusToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [leadMatchesById, setLeadMatchesById] = useState({});
  const [recommendedMatches, setRecommendedMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesError, setMatchesError] = useState("");
  const [leadRowsHydrating, setLeadRowsHydrating] = useState(() => shouldHydrateLeadRows);
  const [leadRowsPublishReady, setLeadRowsPublishReady] = useState(
    () => sourceLeadRows.length > 0
  );
  const lastPublishedLeadRowsRef = useRef("");
  const leadRowsOwnerIdRef = useRef(resolvedCompanyId);

  useEffect(() => {
    if (sourceLeadRows.length) {
      leadRowsOwnerIdRef.current = resolvedCompanyId;
      setLeadRows(sourceLeadRows);
      setLeadRowsHydrating(false);
      setLeadRowsPublishReady(true);
    } else if (expectedLeadCount > 0) {
      setLeadRowsHydrating(true);
      setLeadRowsPublishReady(false);
    } else if (
      resolvedCompanyId > 0 &&
      leadRowsOwnerIdRef.current === resolvedCompanyId &&
      !hasExplicitLeadRowsField &&
      !hasExplicitLeadCountField
    ) {
      // Keep already-hydrated rows visible if a later parent refresh temporarily
      // drops the detailed lead payload from the company object.
      setLeadRowsHydrating(false);
      setLeadRowsPublishReady(false);
    } else {
      // Avoid publishing an initial empty lead list back up to the parent before
      // the company detail fetch has a chance to return the true related leads.
      leadRowsOwnerIdRef.current = resolvedCompanyId;
      setLeadRows(sourceLeadRows);
      setLeadRowsHydrating(false);
      setLeadRowsPublishReady(false);
    }
    setSelectedIds(new Set());
  }, [
    company?.id,
    resolvedCompanyId,
    sourceLeadRows,
    expectedLeadCount,
    reloadKey,
    hasExplicitLeadRowsField,
    hasExplicitLeadCountField,
  ]);

  useEffect(() => {
    if (!resolvedCompanyId || !shouldFetchAuthoritativeLeadRows) return undefined;

    let active = true;
    const controller = new AbortController();
    setLeadRowsHydrating(true);

    fetch(`${API_BASE}/api/companies/${encodeURIComponent(String(resolvedCompanyId))}`, {
      method: "GET",
      headers: {
        ...getAuthHeaders(),
      },
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload?.error || `Failed to load company ${resolvedCompanyId}`);
        }
        return response.json();
      })
      .then((payload) => {
        if (!active) return;
        const hydratedRows = Array.isArray(payload?.company?.related_leads)
          ? payload.company.related_leads.filter(hasLeadTableLabel)
          : [];
        leadRowsOwnerIdRef.current = resolvedCompanyId;
        setLeadRows(hydratedRows);
        setLeadRowsPublishReady(true);
      })
      .catch((error) => {
        if (!active || error?.name === "AbortError") return;
        console.error("Failed to hydrate company leads:", error);
      })
      .finally(() => {
        if (active) setLeadRowsHydrating(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [resolvedCompanyId, shouldFetchAuthoritativeLeadRows, onLeadsChange, reloadKey]);

  useEffect(() => {
    if (!leadRowsPublishReady) return;
    if (leadRowsHydrating && !leadRows.length && shouldHydrateLeadRows) return;
    const nextSignature = buildLeadRowsSignature(leadRows);
    if (lastPublishedLeadRowsRef.current === nextSignature) return;
    lastPublishedLeadRowsRef.current = nextSignature;
    onLeadsChange?.(leadRows);
  }, [leadRows, onLeadsChange, leadRowsHydrating, leadRowsPublishReady, shouldHydrateLeadRows]);

  const visibleLeadRows = useMemo(
    () => leadRows.map((row) => ({ ...row, _company_lead_title: getCompanyLeadTitle(row) })),
    [leadRows]
  );

  const selectedLeadRows = useMemo(
    () => visibleLeadRows.filter((row) => selectedIds.has(getLeadSelectionKey(row))),
    [visibleLeadRows, selectedIds]
  );
  const selectedSyntheticRows = useMemo(
    () => selectedLeadRows.filter((row) => isSyntheticLeadRow(row)),
    [selectedLeadRows]
  );
  const selectedPersistedLeadRows = useMemo(
    () => selectedLeadRows.filter((row) => !isSyntheticLeadRow(row)),
    [selectedLeadRows]
  );
  const hasSyntheticSelection = selectedSyntheticRows.length > 0;
  const statusChangeDisabledMessage = useMemo(() => {
    if (selectedIds.size === 0) return "Please select an item to change status.";
    if (hasSyntheticSelection) {
      return "Status changes for website-imported job rows are available from the Jobs tab.";
    }
    return "";
  }, [hasSyntheticSelection, selectedIds.size]);

  const selectedSequenceItems = useMemo(
    () =>
      selectedLeadRows.map((row) =>
        isSyntheticLeadRow(row)
          ? buildJobSequenceItem(
              {
                ...row,
                id: getLeadJobId(row),
                job_id: getLeadJobId(row),
              },
              "company-leads"
            )
          : buildLeadSequenceItem(row, "company-leads")
      ),
    [selectedLeadRows]
  );

  const toggleLeadSelection = useCallback((leadKey, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const key = String(leadKey);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const applyDeletedSelectionKeys = useCallback((selectionKeys = []) => {
    const keySet = new Set(selectionKeys.map((value) => String(value)));
    setLeadRows((prev) =>
      prev.filter((row) => !keySet.has(getLeadSelectionKey(row)))
    );
    setSelectedIds((prev) => {
      const next = new Set(prev);
      selectionKeys.forEach((value) => next.delete(String(value)));
      return next;
    });
  }, []);

  const deleteLeadIds = useCallback(
    async (ids = []) => {
      const cleanIds = ids
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);

      if (!cleanIds.length) return;

      const response = await fetch(`${API_BASE}/api/leads/bulk-delete`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include",
        body: JSON.stringify({ ids: cleanIds }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Failed to delete lead(s).");
      }
      notifyCompanyRelationshipsChanged();
    },
    []
  );

  const deleteJobsByIds = useCallback(
    async (ids = []) => {
      const cleanIds = ids
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);

      if (!cleanIds.length) return;

      const response = await fetch(`${API_BASE}/api/jobs/bulk-delete`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include",
        body: JSON.stringify({ ids: cleanIds }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Failed to delete job(s).");
      }
      notifyCompanyRelationshipsChanged();
    },
    []
  );

  const handleDeleteOne = useCallback(
    async (leadId) => {
      const confirmed = window.confirm("Delete this lead?");
      if (!confirmed) return;

      try {
        await deleteLeadIds([leadId]);
        applyDeletedSelectionKeys([String(leadId)]);
      } catch (error) {
        console.error("Failed to delete company lead:", error);
        window.alert(error?.message || "Failed to delete this lead.");
      }
    },
    [applyDeletedSelectionKeys, deleteLeadIds]
  );

  const handleBulkDelete = useCallback(async (_event, options = {}) => {
    if (!selectedLeadRows.length) return;

    if (!options?.confirmedByBulkActionsBar) {
      const confirmed = window.confirm(
        `Delete ${selectedLeadRows.length} selected item${selectedLeadRows.length > 1 ? "s" : ""}? This cannot be undone.`
      );
      if (!confirmed) return;
    }

    const deletedSelectionKeys = [];
    const persistedLeadIds = selectedPersistedLeadRows
      .map((row) => getLeadRecordId(row))
      .filter((value) => Number.isFinite(value) && value > 0);
    const syntheticJobIds = selectedSyntheticRows
      .map((row) => getLeadJobId(row))
      .filter((value) => Number.isFinite(value) && value > 0);

    try {
      if (persistedLeadIds.length) {
        await deleteLeadIds(persistedLeadIds);
        deletedSelectionKeys.push(
          ...selectedPersistedLeadRows.map((row) => getLeadSelectionKey(row))
        );
      }
      if (syntheticJobIds.length) {
        await deleteJobsByIds(syntheticJobIds);
        deletedSelectionKeys.push(
          ...selectedSyntheticRows.map((row) => getLeadSelectionKey(row))
        );
      }
      if (deletedSelectionKeys.length) {
        applyDeletedSelectionKeys(deletedSelectionKeys);
      }
    } catch (error) {
      if (deletedSelectionKeys.length) {
        applyDeletedSelectionKeys(deletedSelectionKeys);
      }
      console.error("Failed to bulk delete company leads:", error);
      window.alert(error?.message || "Failed to delete the selected items.");
    }
  }, [
    applyDeletedSelectionKeys,
    deleteJobsByIds,
    deleteLeadIds,
    selectedLeadRows,
    selectedPersistedLeadRows,
    selectedSyntheticRows,
  ]);

  const handleAssignTeamMember = useCallback(async (member) => {
    if (hasSyntheticSelection) {
      throw new Error("Assign website-imported job rows from the Jobs tab.");
    }
    const ids = selectedPersistedLeadRows.map(getLeadRecordId).filter((id) => id > 0);
    if (!ids.length) return;
    const response = await fetch(`${API_BASE}/api/leads/bulk-assign-team-member`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      credentials: "include",
      body: JSON.stringify({
        ids,
        assignee_user_id: member?.id ?? null,
        assignee_name: member?.name || "",
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || "Failed to assign team member.");
    const idSet = new Set(ids.map(String));
    setLeadRows((prev) => prev.map((row) => idSet.has(String(getLeadRecordId(row)))
      ? { ...row, owner: member.name, owner_name: member.name } : row));
    notifyCompanyRelationshipsChanged();
  }, [hasSyntheticSelection, selectedPersistedLeadRows]);

  const handleBulkStatusChange = useCallback(
    async (status) => {
      const nextStatus = String(status || "").trim();
      if (!nextStatus) return;
      if (hasSyntheticSelection) {
        setStatusToast({
          open: true,
          severity: "error",
          message: "Status changes for website-imported job rows are available from the Jobs tab.",
        });
        return;
      }

      const ids = selectedPersistedLeadRows
        .map((row) => getLeadRecordId(row))
        .filter((value) => Number.isFinite(value) && value > 0);

      if (!ids.length) return;

      try {
        const response = await fetch(`${API_BASE}/api/leads/bulk-status`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          credentials: "include",
          body: JSON.stringify({ ids, status: nextStatus }),
        });

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || "Failed to update lead statuses.");
        }

        const idSet = new Set(ids.map((value) => String(value)));
        setLeadRows((prev) =>
          prev.map((row) =>
            idSet.has(String(getLeadRecordId(row))) ? { ...row, status: nextStatus } : row
          )
        );
        setStatusToast({
          open: true,
          severity: "success",
          message: `Updated ${ids.length} lead${ids.length === 1 ? "" : "s"} to "${nextStatus}".`,
        });
      } catch (error) {
        console.error("Failed to update company lead statuses:", error);
        setStatusToast({
          open: true,
          severity: "error",
          message: error?.message || "Failed to update the selected leads.",
        });
      }
    },
    [hasSyntheticSelection, selectedPersistedLeadRows]
  );

  useEffect(() => {
    const leadIds = leadRows
      .map((row) => getLeadRecordId(row))
      .filter((value) => Number.isFinite(value) && value > 0);

    if (!leadIds.length) {
      setLeadMatchesById({});
      setRecommendedMatches([]);
      setMatchesLoading(false);
      setMatchesError("");
      return undefined;
    }

    let active = true;
    const controllers = [];

    const fetchLeadCandidates = async () => {
      setMatchesLoading(true);
      setMatchesError("");

      try {
        const results = await Promise.all(
          leadIds.map(async (leadId) => {
            const controller = new AbortController();
            controllers.push(controller);

            const response = await fetch(
              `${API_BASE}/api/leads/${encodeURIComponent(String(leadId))}/candidates`,
              {
                method: "GET",
                headers: {
                  ...getAuthHeaders(),
                },
                credentials: "include",
                signal: controller.signal,
              }
            );

            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
              return { leadId, rows: [] };
            }

            return {
              leadId,
              rows: Array.isArray(payload?.rows) ? payload.rows : [],
            };
          })
        );

        if (!active) return;

        const nextTopMatches = {};
        const recommended = [];
        const seenCandidates = new Set();

        results.forEach(({ leadId, rows = [] }) => {
          const matches = rows
            .map((row, index) => ({
              id: row?.candidate_id ?? row?.id ?? `${leadId}-${index}`,
              leadId,
              name: safeText(row?.candidate_name, "Candidate"),
              title: safeText(row?.candidate_title, "Candidate"),
              company: safeText(row?.candidate_company, "Company"),
              location: safeText(row?.candidate_location),
              status: safeText(
                row?.candidate_stage || row?.candidate_status || row?.last_sync_label,
                "Matched"
              ),
              avatarUrl: resolveAssetUrl(row?.avatar_url || ""),
              email: safeText(row?.email),
              linkedinUrl: normalizeContactSocialUrl("linkedin", row?.linkedin_url),
              skills: parseSkills(row?.skills_json).slice(0, 3),
              extraSkillsCount: Number(row?.extra_skills_count || 0),
              recruiterName: safeText(row?.recruiter_name),
              timestamp: row?.updated_at || row?.created_at || null,
            }))
            .filter((row) => row.name);

          nextTopMatches[String(leadId)] = matches[0] || null;

          matches.forEach((match) => {
            const candidateKey = `${match.id}-${match.name.toLowerCase()}`;
            if (seenCandidates.has(candidateKey)) return;
            seenCandidates.add(candidateKey);
            recommended.push(match);
          });
        });

        setLeadMatchesById(nextTopMatches);
        setRecommendedMatches(recommended.slice(0, 3));
      } catch (error) {
        if (!active || error?.name === "AbortError") return;
        console.error("Failed to load company lead matches:", error);
        setLeadMatchesById({});
        setRecommendedMatches([]);
        setMatchesError("Unable to load recommended matches.");
      } finally {
        if (active) setMatchesLoading(false);
      }
    };

    fetchLeadCandidates();

    return () => {
      active = false;
      controllers.forEach((controller) => controller.abort());
    };
  }, [leadRows]);

  const tableColumns = useMemo(
    () => [
      {
        id: "_select",
        label: "",
        numeric: false,
        sortable: false,
        width: TABLE_SELECTION_COLUMN_WIDTH_PX,
        minWidth: TABLE_SELECTION_COLUMN_WIDTH_PX,
        maxWidth: TABLE_SELECTION_COLUMN_WIDTH_PX,
      },
      { id: "_company_lead_title", label: "Job Title", numeric: false, minWidth: 220 },
      { id: "status", label: "Status", numeric: false },
      { id: "hiring_manager_name", label: "Hiring Manager", numeric: false, minWidth: 150 },
      { id: "source", label: "Source", numeric: false },
      { id: "created_at", label: "Posted On", numeric: false },
      { id: "top_match", label: "Top Best Match", numeric: false },
      { id: "_actions", label: "", numeric: false, sortable: false },
    ],
    []
  );

  const openLeadDetails = useCallback(
    (row) => {
      if (!row || typeof onOpenLeadDetails !== "function") return;
      onOpenLeadDetails(getNavigableLeadRow(row), "details", {
        parentCompany: company,
        parentTab: "leads",
      });
    },
    [company, onOpenLeadDetails]
  );

  const canOpenLeadRowDetails = useCallback(
    (row) =>
      Boolean(onOpenLeadDetails) &&
      (getLeadRecordId(row) > 0 || getLeadJobId(row) > 0),
    [onOpenLeadDetails]
  );

  const renderLeadRow = useCallback(
    (row) => {
      const leadRecordId = getLeadRecordId(row);
      const selectionKey = getLeadSelectionKey(row);
      const topMatch = leadRecordId > 0 ? leadMatchesById[String(leadRecordId)] || null : null;
      const primaryLabel = getCompanyLeadTitle(row);
      const isSyntheticRow = leadRecordId <= 0;
      const canOpenLeadDetails = canOpenLeadRowDetails(row);
      const statusStyle = statusChipSx(row?.status);
      const hiringManager =
        row?.hiring_manager_name ||
        row?.hiring_manager ||
        row?.contact_name ||
        row?.contactName ||
        "-";

      return (
        <>
          <TableCell
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            sx={{
              width: TABLE_SELECTION_COLUMN_WIDTH_PX,
              minWidth: TABLE_SELECTION_COLUMN_WIDTH_PX,
              maxWidth: TABLE_SELECTION_COLUMN_WIDTH_PX,
              pl: `${TABLE_SELECTION_COLUMN_INSET_PX}px`,
              pr: 0,
              pt: 1.25,
              pb: 1.25,
              verticalAlign: "top",
            }}
          >
            <Checkbox
              checked={selectedIds.has(selectionKey)}
              onChange={(event) => toggleLeadSelection(selectionKey, event.target.checked)}
              onClick={(event) => event.stopPropagation()}
              sx={{
                p: 0,
                width: TABLE_SELECTION_CHECKBOX_SIZE_PX,
                height: TABLE_SELECTION_CHECKBOX_SIZE_PX,
                minWidth: TABLE_SELECTION_CHECKBOX_SIZE_PX,
                minHeight: TABLE_SELECTION_CHECKBOX_SIZE_PX,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                "& .MuiSvgIcon-root": { fontSize: TABLE_SELECTION_CHECKBOX_SIZE_PX },
              }}
            />
          </TableCell>

          <TableCell>
            <Stack spacing={0.2}>
              <Typography
                component="button"
                type="button"
                sx={{
                  width: "fit-content",
                  border: 0,
                  background: "transparent",
                  p: 0,
                  m: 0,
                  fontSize: 12,
                  fontWeight: 500,
                  color: canOpenLeadDetails ? "#344054" : "#344054",
                  cursor: canOpenLeadDetails ? "pointer" : "default",
                  textDecoration: "none",
                  textAlign: "left",
                  "&:hover": canOpenLeadDetails
                    ? {
                        textDecoration: "underline",
                      }
                    : undefined,
                }}
                onClick={(event) => {
                  if (!canOpenLeadDetails) return;
                  event.stopPropagation();
                  void openLeadDetails(row);
                }}
              >
                {primaryLabel}
              </Typography>
            </Stack>
          </TableCell>

          <TableCell>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: 11,
                color: statusStyle.color,
              }}
            >
              {row?.status || "No stage"}
            </Typography>
          </TableCell>

          <TableCell>
            <Typography sx={{ fontSize: 11, color: "#475467" }}>
              {hiringManager}
            </Typography>
          </TableCell>

          <TableCell>
            <Typography sx={{ fontSize: 11, color: "#475467" }}>
              {row?.source || "-"}
            </Typography>
          </TableCell>

          <TableCell>
            <Typography sx={{ fontSize: 11, color: "#667085" }}>
              {formatTimeAgo(row?.created_at || row?.updated_at || row?.last_sync)}
            </Typography>
          </TableCell>

          <TableCell>
            {topMatch ? (
              <Stack direction="row" spacing={0.7} alignItems="center">
                <Avatar
                  src={topMatch.avatarUrl || undefined}
                  sx={{ width: 24, height: 24, fontSize: 10, bgcolor: "#F4EBFF", color: TAB_PURPLE }}
                >
                  {topMatch.avatarUrl ? null : safeText(topMatch.name).slice(0, 1).toUpperCase()}
                </Avatar>
                <Typography sx={{ fontSize: 11, fontWeight: 500, color: "#475467" }}>
                  {topMatch.name}
                </Typography>
              </Stack>
            ) : (
              <Typography sx={{ fontSize: 11, color: "#98A2B3" }}>No matches</Typography>
            )}
          </TableCell>

          <TableCell
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            sx={{ width: 56 }}
          >
            {isSyntheticRow ? null : (
              <RowMenu
                canDelete={isAdminUser}
                onDelete={() => handleDeleteOne(leadRecordId)}
              />
            )}
          </TableCell>
        </>
      );
    },
    [
      handleDeleteOne,
      leadMatchesById,
      canOpenLeadRowDetails,
      isAdminUser,
      openLeadDetails,
      selectedIds,
      toggleLeadSelection,
    ]
  );

  const getLeadRowProps = useCallback(
    (row) => {
      if (!canOpenLeadRowDetails(row)) return undefined;
      if (typeof onOpenLeadDetails !== "function") return undefined;

      return {
        onClick: () => {
          void openLeadDetails(row);
        },
        onKeyDown: (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          void openLeadDetails(row);
        },
        role: "button",
        tabIndex: 0,
        "aria-label": `Open lead ${getCompanyLeadTitle(row)}`,
        sx: {
          cursor: "pointer",
          "&:focus-visible": {
            outline: "2px solid rgba(156, 79, 255, 0.45)",
            outlineOffset: "-2px",
          },
        },
      };
    },
    [canOpenLeadRowDetails, onOpenLeadDetails, openLeadDetails]
  );

  return (
    <Box sx={TAB_VIEW_CONTENT_WRAPPER_SX}>
      <CompanyTabSectionHeader
        title="Leads"
        actions={[
          { label: "Add New", onClick: onAddNewLead },
        ]}
      />

      {leadRowsHydrating && !leadRows.length ? (
        <Box
          sx={{
            minHeight: 148,
            borderRadius: "12px",
            border: "1.5px dashed #C9D3DE",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            py: 4,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : !leadRows.length ? (
        <Box
          sx={{
            minHeight: 148,
            borderRadius: "12px",
            border: "1.5px dashed #C9D3DE",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            py: 4,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              variant="contained"
              className={withPillButtonClassName()}
              onClick={onAddNewLead}
              startIcon={<AddCircleOutlineIcon sx={{ fontSize: 17 }} />}
              sx={emptyActionButtonSx}
            >
              Add New Lead
            </Button>
            <Button
              variant="contained"
              className={withPillButtonClassName()}
              onClick={onSearchLead}
              startIcon={<SearchIcon sx={{ fontSize: 17 }} />}
              sx={emptyActionButtonSx}
            >
              Search New Lead
            </Button>
          </Stack>
        </Box>
      ) : !visibleLeadRows.length ? (
        <Box
          sx={{
            minHeight: 148,
            borderRadius: "12px",
            border: "1.5px dashed #C9D3DE",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            py: 4,
          }}
        >
          <Stack spacing={1.25} alignItems="center">
            <Typography sx={{ fontSize: 14, color: "#6B7280", textAlign: "center" }}>
              No leads are available for this company.
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Box>
          <LeadBulkActionsBar
            selectedCount={selectedIds.size}
            onAddToSequence={() => setIsAddToSequenceOpen(true)}
            onBulkDelete={handleBulkDelete}
            onAssignTeamMember={handleAssignTeamMember}
            disableAssignment={hasSyntheticSelection}
            onChangeStatus={handleBulkStatusChange}
            statusFeedback={statusToast}
            onStatusFeedbackClose={() =>
              setStatusToast((prev) => ({ ...prev, open: false }))
            }
            showDelete={isAdminUser}
            disableStatusChange={selectedIds.size === 0 || hasSyntheticSelection}
            statusDisabledMessage={statusChangeDisabledMessage}
          />

          <Box sx={{ borderTop: "1px solid #E5E7EB", borderBottom: "1px solid #E5E7EB" }}>
            <GenericDataTable
              columns={tableColumns}
              rows={visibleLeadRows}
              renderRow={renderLeadRow}
              getRowProps={getLeadRowProps}
              bulkActionsTop={null}
              bulkActionsBottom={null}
              defaultOrderBy="created_at"
              defaultOrderDirection="desc"
              includeSelectionColumn={false}
              initialRowsPerPage={100}
              showResultsHeader={false}
              paginationBarSx={{ display: "none" }}
            />
          </Box>

          <LeadBulkActionsBar
            selectedCount={selectedIds.size}
            onAddToSequence={() => setIsAddToSequenceOpen(true)}
            onBulkDelete={handleBulkDelete}
            onAssignTeamMember={handleAssignTeamMember}
            disableAssignment={hasSyntheticSelection}
            onChangeStatus={handleBulkStatusChange}
            statusFeedback={statusToast}
            onStatusFeedbackClose={() =>
              setStatusToast((prev) => ({ ...prev, open: false }))
            }
            showDelete={isAdminUser}
            disableStatusChange={selectedIds.size === 0 || hasSyntheticSelection}
            statusDisabledMessage={statusChangeDisabledMessage}
          />

          <CompanyTabSectionHeader
            title="Recommended Matches"
            actions={[
              { label: "Add New" },
              { label: "View all" },
            ]}
          />

          {matchesError ? (
            <Typography sx={{ mb: 2, fontSize: 12, color: "#B45309" }}>
              {matchesError}
            </Typography>
          ) : null}

          {matchesLoading && !recommendedMatches.length ? (
            <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={26} />
            </Box>
          ) : recommendedMatches.length ? (
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "repeat(1, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                },
              }}
            >
              {recommendedMatches.map((match) => {
                const initials = safeText(match.name)
                  .split(/\s+/)
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((part) => part[0]?.toUpperCase() || "")
                  .join("");

                return (
                  <Box
                    key={`${match.id}-${match.name}`}
                    sx={{
                      border: "1px solid #E5E7EB",
                      borderRadius: 2,
                      backgroundColor: "#FFFFFF",
                      p: 2,
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                      <Avatar
                        src={match.avatarUrl || undefined}
                        sx={{
                          width: 38,
                          height: 38,
                          bgcolor: "#F5F3FF",
                          color: TAB_PURPLE,
                          fontWeight: 700,
                        }}
                      >
                        {!match.avatarUrl ? initials || "C" : null}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: TAB_PURPLE }}>
                          {match.name}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                          {match.title}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: "#98A2B3" }}>
                          {match.company}
                        </Typography>
                      </Box>
                    </Stack>

                    <Box sx={{ minHeight: 58, mb: 1.5 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#98A2B3" }}>
                        Status:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: candidateStatusColor(match.status),
                          mt: 0.35,
                        }}
                      >
                        {match.status}
                      </Typography>
                    </Box>

                    <Box sx={{ minHeight: 70, mb: 1.5 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#98A2B3", mb: 0.8 }}>
                        Skills:
                      </Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {match.skills.map((skill) => (
                          <Chip
                            key={skill}
                            label={skill}
                            size="small"
                            sx={{
                              height: 22,
                              borderRadius: "999px",
                              backgroundColor: "#EEF2FF",
                              color: TAB_BLUE,
                              fontSize: 11,
                            }}
                          />
                        ))}
                        {match.extraSkillsCount > 0 ? (
                          <Typography sx={{ fontSize: 11, color: "#6B7280", alignSelf: "center" }}>
                            +{match.extraSkillsCount}
                          </Typography>
                        ) : null}
                      </Stack>
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#98A2B3", mb: 0.9 }}>
                        Quick Actions:
                      </Typography>
                      <Stack direction="row" spacing={0.8}>
                        <IconButton
                          size="small"
                          component={match.email ? "a" : "span"}
                          href={match.email ? `mailto:${match.email}` : undefined}
                          disableRipple={!match.email}
                          sx={actionIconButtonSx(Boolean(match.email))}
                        >
                          <Sms size={15} color={TAB_PURPLE} />
                        </IconButton>
                        <IconButton
                          size="small"
                          disableRipple
                          sx={actionIconButtonSx(false)}
                        >
                          <Call size={15} color={TAB_PURPLE} />
                        </IconButton>
                        <IconButton
                          size="small"
                          component={match.linkedinUrl ? "a" : "span"}
                          href={match.linkedinUrl || undefined}
                          target={match.linkedinUrl ? "_blank" : undefined}
                          rel={match.linkedinUrl ? "noopener noreferrer" : undefined}
                          disableRipple={!match.linkedinUrl}
                          sx={actionIconButtonSx(Boolean(match.linkedinUrl))}
                        >
                          <LinkedinBrandIcon
                            size={18}
                            color={TAB_PURPLE}
                            foregroundColor="#FFFFFF"
                            showBackground={true}
                          />
                        </IconButton>
                      </Stack>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box
              sx={{
                minHeight: 132,
                borderRadius: "12px",
                border: "1.5px dashed #C9D3DE",
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
              }}
            >
              <Typography sx={{ fontSize: 14, color: "#6B7280" }}>
                No recommended candidate matches are saved for this company yet.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <AddToSequenceDialog
        open={isAddToSequenceOpen}
        onClose={() => setIsAddToSequenceOpen(false)}
        entityType="lead"
        items={selectedSequenceItems}
        onCreated={() => setSelectedIds(new Set())}
      />
    </Box>
  );
}
